import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  ApplicationState,
  getPasswordRecoveryManagement,
  getRecoveryMethodError,
  getRecoveryMethodSuccess,
  getSelectedMailbox,
} from '../../../store/reducers';
import * as RecoveryMethodsActions from '../../store/actions/password-recovery-management.actions';
import {
  PasswordRecoveryManagement,
  Status,
} from '../../models/password-recovery-management.model';
import { Alert } from '@proximus/ng-common-components';
import { SuccessCode, SuccessInfo } from '../../models/success.model';
import { ErrorCode, TechnicalError } from '../../models/errors.model';
import { AlertsUtilService } from '../../service/alerts.util.service';
import { InceptionService } from '@proximus/ng-common';
import { filter } from 'rxjs/operators';
import { BehaviorSubject, Observable, of, Subject, Subscription } from 'rxjs';

@Component({
  selector: 'mprd-password-recovery-management',
  templateUrl: 'password-recovery-management-container.component.html',
})
export class PasswordRecoveryManagementContainerComponent
  implements OnInit, OnDestroy
{
  emailStatus: Status;
  smsStatus: Status;
  hasNoRecoveryMethods: boolean;
  showMobileAddContainer: boolean;
  showMobileActiveContainer: boolean;
  showEmailAddContainer: boolean;
  showEmailActiveContainer: boolean;
  showLineSeparatorBetweenComponents: boolean;
  alerts$: Observable<Alert[]>;
  alerts: Alert[] = [];
  recoveryMethodErrorSubscription: Subscription;
  recoveryMethodSuccessSubscription: Subscription;

  loading$: Subject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(
    private store: Store<ApplicationState>,
    private alertsUtilService: AlertsUtilService,
    private changeDetector: ChangeDetectorRef,
    private inceptionService: InceptionService,
    private elementRef: ElementRef,
  ) {}

  ngOnInit() {
    setTimeout(() => {
      this.inceptionService.panelAccordion(this.elementRef);
      this.inceptionService.init();
    });
    this.store.select(getSelectedMailbox).subscribe((mailbox) => {
      this.loading$.next(true);
      this.store.dispatch(
        new RecoveryMethodsActions.FindRecoveryMethodsAction(),
      );
      this.clearErrorAlerts();
      this.clearSuccessAlerts();
    });

    this.store
      .select(getPasswordRecoveryManagement)
      .pipe(filter(Boolean))
      .subscribe((passwordRecoveryManagement: PasswordRecoveryManagement) => {
        this.loading$.next(false);

        this.hasNoRecoveryMethods =
          !passwordRecoveryManagement.email && !passwordRecoveryManagement.sms
            ? true
            : false;

        if (!this.hasNoRecoveryMethods) {
          this.emailStatus = passwordRecoveryManagement.email
            ? passwordRecoveryManagement.email.status
            : null;
          this.smsStatus = passwordRecoveryManagement.sms
            ? passwordRecoveryManagement.sms.status
            : null;
          this.showMobileAddContainer = !this.smsStatus ? true : false;
          this.showMobileActiveContainer =
            this.smsStatus === 'active' ? true : false;
          this.showEmailAddContainer = !this.emailStatus ? true : false;
          this.showEmailActiveContainer =
            this.emailStatus === 'active' ? true : false;

          this.showLineSeparatorBetweenComponents =
            (this.smsStatus === 'pending' && this.emailStatus === 'pending') ||
            this.showEmailAddContainer ||
            this.showMobileAddContainer ||
            this.showEmailActiveContainer ||
            this.showMobileActiveContainer;
        }
      });

    this.recoveryMethodErrorSubscription = this.store
      .select(getRecoveryMethodError)
      .pipe(filter(Boolean))
      .subscribe((error: TechnicalError) => {
        this.updateRecoveryMethodError(error);
      });
    this.recoveryMethodSuccessSubscription = this.store
      .select(getRecoveryMethodSuccess)
      .pipe(filter(Boolean))
      .subscribe((success: SuccessInfo) => {
        this.updateRecoveryMethodSuccessInfo(success);
      });
    this.alerts$ = of(this.alerts);
  }

  private updateRecoveryMethodError(technicalError: TechnicalError) {
    switch (technicalError.code) {
      case ErrorCode.InvalidToken:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            false,
            'activate.sms.failure.message',
            technicalError.code,
          ),
        );
        break;
      case ErrorCode.MailboxUnknown:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            false,
            'mailbox.unknown.message',
            technicalError.code,
          ),
        );
        break;
      case ErrorCode.TokenDoesNotMatch:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            false,
            'password.recovery.sms.token.does.not.match.message',
            technicalError.code,
          ),
        );
        break;
      case ErrorCode.TokenNotFound:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            false,
            'password.recovery.sms.token.not.found.message',
            technicalError.code,
          ),
        );
        break;
      case ErrorCode.InvalidMsisdn:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            false,
            'password.recovery.invalid.msisdn.message',
            technicalError.code,
          ),
        );
        break;
      case ErrorCode.AddressMalformed:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            false,
            'password.recovery.address.malformed.message',
            ErrorCode.RecoveryAddressMalformed,
          ),
        );
        break;
      case ErrorCode.EmailValidationError:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'error',
            false,
            'email.validation.server.error',
            ErrorCode.EmailValidationError,
          ),
        );
        break;
    }
    if (technicalError.code === undefined) {
      this.addAlert(
        this.alertsUtilService.getAlertFor(
          'error',
          false,
          'general.technical.error.message',
          ErrorCode.TechnicalError,
        ),
      );
    }
    this.clearSuccessAlerts();
  }

  private clearSuccessAlerts() {
    this.removeAlertByCode(SuccessCode.PasswordRecoveryEmailMethodSuccess);
    this.removeAlertByCode(
      SuccessCode.PasswordRecoverySmsMethodActivateSuccess,
    );
    this.removeAlertByCode(SuccessCode.PasswordRecoverySmsMethodSuccess);
  }

  private updateRecoveryMethodSuccessInfo(successInfo: SuccessInfo) {
    switch (successInfo.code) {
      case SuccessCode.PasswordRecoveryEmailMethodSuccess:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'success',
            false,
            'password.recovery.email.method.add.success.message',
            successInfo.code,
          ),
        );
        break;
      case SuccessCode.PasswordRecoverySmsMethodActivateSuccess:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'success',
            false,
            'password.recovery.sms.method.activate.success.message',
            successInfo.code,
          ),
        );
        this.removeAlertByCode(SuccessCode.PasswordRecoverySmsMethodSuccess);
        break;
      case SuccessCode.PasswordRecoverySmsMethodSuccess:
        this.addAlert(
          this.alertsUtilService.getAlertFor(
            'success',
            false,
            'password.recovery.sms.method.add.success.message',
            successInfo.code,
          ),
        );
        this.removeAlertByCode(
          SuccessCode.PasswordRecoverySmsMethodActivateSuccess,
        );
        break;
    }
    this.clearErrorAlerts();
  }

  private clearErrorAlerts() {
    this.removeAlertByCode(ErrorCode.InvalidToken);
    this.removeAlertByCode(ErrorCode.MailboxUnknown);
    this.removeAlertByCode(ErrorCode.TokenDoesNotMatch);
    this.removeAlertByCode(ErrorCode.TokenNotFound);
    this.removeAlertByCode(ErrorCode.InvalidMsisdn);
    this.removeAlertByCode(ErrorCode.RecoveryAddressMalformed);
    this.removeAlertByCode(ErrorCode.TechnicalError);
  }

  private removeAlertByCode(code: SuccessCode | ErrorCode): void {
    if (this.alerts.length > 0) {
      const previousAlerts = this.alerts.filter((alert) => alert.id === code);
      if (previousAlerts.length > 0) {
        this.alerts.splice(this.alerts.indexOf(previousAlerts[0]), 1);
      }
    }
    this.changeDetector.markForCheck();
  }

  private addAlert(alert: Alert) {
    if (
      alert &&
      !this.alerts.find((existingAlert) => existingAlert.id === alert.id)
    ) {
      this.alerts.push(alert);
    }
    this.changeDetector.markForCheck();
  }

  onCloseAlert(alert: Alert) {
    switch (alert.type) {
      case 'success':
        this.store.dispatch(new RecoveryMethodsActions.ResetSuccess());
        break;
      case 'error':
        this.store.dispatch(new RecoveryMethodsActions.ResetError());
        break;
    }
  }

  ngOnDestroy() {
    this.recoveryMethodSuccessSubscription.unsubscribe();
    this.recoveryMethodErrorSubscription.unsubscribe();
  }
}
